class HHDeliveryTimeline extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.sectionId) {
      this.dataset.sectionId = this.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || '';
    }

    if (this.dataset.relocated !== 'true') {
      queueMicrotask(() => this.relocate());
    }

    this.startWatching();

    this.tickCountdown();
    this._interval = setInterval(() => this.tickCountdown(), 30000);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onFormClick);
    clearInterval(this._interval);
  }

  relocate() {
    if (this.dataset.relocated === 'true' || !this.isConnected) return;

    const beforeAccordion = document.querySelector('.st_pi-row');
    if (beforeAccordion && beforeAccordion.parentNode) {
      this.dataset.relocated = 'true';
      beforeAccordion.parentNode.insertBefore(this, beforeAccordion);
      return;
    }

    const buyButtons =
      document.querySelector('.product-form-buttons') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('.st_pi-form');

    if (buyButtons && buyButtons.parentNode) {
      this.dataset.relocated = 'true';
      buyButtons.parentNode.insertBefore(this, buyButtons.nextSibling);
    }
  }

  /**
   * Same reasoning as hh-fast-ship-status.js: Sternify's pill picker never
   * dispatches the theme's native variant:update event, lives outside
   * .st_pi-form entirely, replaces the id input's DOM node on its own
   * re-renders, and doesn't update it perfectly synchronously with the click
   * - a fixed-delay single check raced it. Polling until the value actually
   * changes sidesteps all of that.
   */
  getIdInput() {
    return (
      document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]') ||
      document.querySelector('.st_pi-form input[name="id"]')
    );
  }

  startWatching() {
    this._onFormClick = () => {
      const idAtClickTime = this.getIdInput()?.value;
      this.pollForChange(idAtClickTime, 0);
    };
    document.addEventListener('click', this._onFormClick);
  }

  pollForChange(previousId, attempts) {
    if (attempts > 20) return;

    setTimeout(() => {
      const currentId = this.getIdInput()?.value;
      if (currentId && currentId !== previousId) {
        this.fetchAndRender(currentId);
      } else {
        this.pollForChange(previousId, attempts + 1);
      }
    }, 100);
  }

  /**
   * The public /products/{handle}.js endpoint doesn't expose real inventory
   * counts, only an `available` boolean that's true for both real stock and
   * build-to-order variants - it can't tell them apart. The Section
   * Rendering API asks the server to redo the same in_stock/cutoff Liquid
   * this section already runs on first load, with real per-variant
   * inventory data, for the variant named in the `variant` query param.
   */
  async fetchAndRender(variantId) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    try {
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('hh-delivery-timeline');
      if (!fresh) return;

      this.dataset.inStock = fresh.dataset.inStock;
      this.dataset.cutoffTs = fresh.dataset.cutoffTs;
      this.innerHTML = fresh.innerHTML;

      this.tickCountdown();
    } catch (err) {
      console.error('[hh-delivery-timeline]', err);
    }
  }

  tickCountdown() {
    const countdownValue = this.querySelector('[data-countdown-value]');
    if (!countdownValue) return;

    const cutoffMs = Number(this.dataset.cutoffTs);
    const diff = Math.max(0, cutoffMs - Date.now());
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    countdownValue.textContent = `${hours} ${this.dataset.hoursLabel} ${minutes} ${this.dataset.minutesLabel}`;
  }
}

if (!customElements.get('hh-delivery-timeline')) {
  customElements.define('hh-delivery-timeline', HHDeliveryTimeline);
}
